<script setup lang="ts">
/**
 * Bordered tag-field container: the standard input chrome (same tokens as the
 * scoped input CSS in `style.css`) around an inline flow of chips plus a
 * de-chromed input, scrolling vertically past `maxHeight`. Layout only —
 * picking/removing behavior stays with the consumer. `stack` picks the chip
 * layout: vertical wrap+scroll (default) or one horizontally scrolling row.
 *
 * Mechanics (reviewer-adjudicated, verified against the compiled bundle):
 * - Inner inputs are de-chromed by NEUTERING the input tokens on the flow's
 *   children (custom properties inherit; no specificity fight with the scoped
 *   input rules or NxInputWrapper's :focus-within rule). This is how
 *   `.nx-ghost-cell` actually wins (Table/shell.css:16-17).
 * - Chrome and scrollport are SEPARATE nodes: an outset focus ring on an
 *   `overflow-y: auto` element would be clipped by its own scrollport.
 * - The field ring lights via `:has(input:focus-visible)` /
 *   `:has(input[data-focus-visible])` — NOT `:focus-within`, which would light
 *   it while a chip button is focused. NxAutocompleteInput sets
 *   `data-focus-visible` while its list is open.
 * - Mousedown on non-interactive chrome focuses the slotted input
 *   (preventDefault so native focus handling doesn't fight it).
 *
 * Known v1 limitations (accepted): the inner input keeps its inline
 * `padding-inline-end` (NxInputWrapper writes it as an inline style); scrolling
 * the flow with an open autocomplete list logs a dev "readonly computed"
 * warning from NxDropdown's close wiring (a no-op close — pre-existing).
 * Do not nest other input fields inside the slot — they would be de-chromed too.
 */
withDefaults(
  defineProps<{
    /** Vertical scroll cap for the chip+input flow (em: follows density scale). */
    maxHeight?: string;
    /**
     * Chip layout: 'vertical' (default) wraps chips into rows and scrolls
     * vertically past maxHeight; 'horizontal' keeps a slotted NxChipGroup in
     * ONE horizontally scrolling row with the input inline at its end (a thin
     * cursor slot that grows only when the chips leave room).
     */
    stack?: 'vertical' | 'horizontal';
  }>(),
  { maxHeight: '5.5em', stack: 'vertical' }
);

function onChromeMousedown(e: MouseEvent) {
  const target = e.target as HTMLElement;
  // Only empty chrome: clicks on chips, buttons, or the input itself keep
  // their native behavior.
  if (target.closest('button, input, a, [role="option"]')) return;
  const input = (e.currentTarget as HTMLElement).querySelector('input');
  if (input) {
    e.preventDefault();
    input.focus();
  }
}
</script>

<template>
  <div
    class="nx-tag-field"
    :class="{ 'nx-tag-field--h-stack': stack === 'horizontal' }"
    :style="{ '--nxt-tag-field-max-height': maxHeight }"
    @mousedown="onChromeMousedown"
  >
    <div class="nx-tag-field-flow">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.nx-tag-field {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background: var(--nxt-input-bg);
  border: var(--nxt-hairline) solid var(--nxt-input-border);
  border-radius: var(--nxt-radius-md);
  transition:
    border-color 0.1s,
    background-color 0.1s,
    box-shadow 0.1s;
}
.nx-tag-field:hover:not(:focus-within) {
  border-color: var(--nxt-input-border-hover);
}
.nx-tag-field:has(input:focus-visible),
.nx-tag-field:has(input[data-focus-visible]) {
  border-color: var(--nxt-input-border-focus);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-input-ring);
}
.nx-tag-field-flow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  max-height: var(--nxt-tag-field-max-height);
  /* Vertical scroll past maxHeight; horizontal scroll only when a single
     unbreakable item (one long chip) exceeds the field width — wrapping
     handles everything else. */
  overflow-y: auto;
  overflow-x: auto;
  /* Single-line state matches the standard text-input height (the de-chromed
     input below drops its own block padding): the input formula is the
     control line (--nxt-line-height-control) at font-sm + 2 * space-xs
     padding (+ the outer border on the root). font-size is pinned to font-sm so the em-based tokens compute
     exactly like they do on the sibling inputs; border-box so the flow's own
     padding cannot drift the total. */
  font-size: var(--nxt-font-size-sm);
  box-sizing: border-box;
  min-height: calc(
    var(--nxt-line-height-control) * var(--nxt-font-size-sm) + 2 * var(--nxt-space-xs)
  );
  min-width: 0;
}
</style>

<style>
/* Unscoped, keyed on our own class. Token neutering (the ghost-cell kill
   switch, Table/shell.css:16-17): custom properties inherit down to the input
   three levels deep, so every scoped chrome rule — resting, hover,
   :focus-visible, [data-focus-visible], and NxInputWrapper's :focus-within —
   paints transparent. border-color stays reserved at 1px, nothing shifts.
   Scoped to the flow's CHILDREN: the field's own chrome above reads the real
   tokens on the root. */
.nx-tag-field-flow > * {
  --nxt-input-bg: transparent;
  --nxt-input-border: transparent;
  --nxt-input-border-hover: transparent;
  --nxt-input-border-focus: transparent;
  --nxt-input-ring: transparent;
}
/* The autocomplete's anchor joins the flex flow: inline after the chips while
   there is room (its scoped width:100% must yield), a full line of its own
   when chips crowd it — which also keeps the anchor-sized dropdown list at
   least half the field wide. The wrapper inside stays width:100%/relative, so
   its absolutely positioned icon/action machinery is unaffected. */
.nx-tag-field-flow > .nx-dropdown-anchor {
  flex: 1 1 8rem;
  min-width: 50%;
  width: auto;
}
/* Horizontal stack: a slotted NxChipGroup becomes ONE nowrap row that scrolls
   sideways; chips must not shrink or wrap internally. The width cap leaves the
   input a 2rem cursor reserve ON THE SAME LINE — the flow wraps items at their
   un-shrunk (hypothetical) sizes, so without it a full chip row would push the
   input to its own line. */
.nx-tag-field--h-stack .nx-tag-field-flow > .nx-chip-group {
  flex-wrap: nowrap;
  overflow-x: auto;
  max-width: calc(100% - calc(var(--nxt-size-unit) * 2) - 2 * var(--nxt-space-2xs));
  min-width: 0;
}
.nx-tag-field--h-stack .nx-tag-field-flow > .nx-chip-group > .nx-chip {
  flex: none;
}
/* The input sits inline RIGHT of the chips: the own-line rule above
   (flex-basis 8rem + min-width 50%) yields to the cursor reserve — the BASIS
   must shrink too, since the flow wraps at hypothetical sizes. */
.nx-tag-field--h-stack .nx-tag-field-flow > .nx-dropdown-anchor {
  flex: 1 1 calc(var(--nxt-size-unit) * 2);
  min-width: calc(var(--nxt-size-unit) * 2);
}
/* The de-chromed input drops its own block padding so the single-line field
   height matches sibling controls (selects/inputs use --nxt-control-size-sm).
   (0,2,1) beats the scoped resting rule's (0,2,0); no state variant touches
   padding. */
.nx-tag-field input.height-xs,
.nx-tag-field input.height-s,
.nx-tag-field input.height-m {
  padding-block: 0;
}
</style>
